import React from "react";
import { Card, Table, Layout, PageHeader, Button, Space, Descriptions, Tag } from "antd";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import { fmtDateTime, formatHours } from "../../common/Utils";
import { volunteersStore } from "../../store/volunteer/VolunteersStore";
import { VolunteerDlg } from "./components/VolunteerDlg";
import moment from "moment-timezone";
import dayjs from "dayjs";
import { UserLink } from "../../components/UserLink/UserLink";
import { myHistory } from "../../common/History";

const fmtTimes = (s: string, e: string) =>
  `${moment(s, "hh:mm").format("h:mma")}-${moment(e, "hh:mm").format("h:mma")}`;

export const Volunteer = observer(() => {
  let { id } = useParams<any>();

  const { viewStore, editStore } = volunteersStore;
  viewStore.active(id);

  const isDeadline = !!viewStore.volunteer?.deadline && !!viewStore.volunteer?.maxHours;

  const columns = [
    { title: "", key: "i", render: (t, r, i) => i + 1 },
    {
      title: "User Full Name",
      dataIndex: "parent",
      key: "parent",
      sorter: (a, b) => a.parent.name.localeCompare(b.parent.name),
      render: (r) => <UserLink userId={r.id} name={r.name} fullName={r.name} />,
    },
    {
      title: "Participants",
      dataIndex: "participants",
      key: "participants",
      sorter: (a, b) => a.localeCompare(b),
    },
    {
      title: isDeadline ? "Deadline" : "Date",
      key: "date",
      render: (_, r) => {
        if (isDeadline) {
          return moment(r.deadline, "YYYY-MM-DD").format("MMM D");
        } else {
          return r.schedule?.map((d) => moment(d, "YYYY-MM-DD").format("MMM D")).join(", ");
        }
      },
      sorter: (a, b) => {
        let aDate = isDeadline ? a.deadline : a.schedule[0];
        let bDate = isDeadline ? b.deadline : b.schedule[0];
        return aDate.localeCompare(bDate);
      },
    },
    {
      title: isDeadline ? "Hours" : "Time",
      key: "time",
      dataIndex: "options",
      render: (_, r) => {
        if (isDeadline) {
          if (r.servingHours && r.status === "Completed") {
            return formatHours(Math.min(r.servingHours, r.maxHours));
          }
          return `Max ${formatHours(r.maxHours)}`;
        } else {
          return r.options?.map((d) => fmtTimes(d.startTime, d.endTime)).join("\n");
        }
      },
      sorter: (a, b) => {
        if (isDeadline) {
          return Math.min(a.servingHours || 9999, a.maxHours) - Math.min(b.servingHours || 9999, b.maxHours);
        } else {
          return a.options?.[0].startTime.localeCompare(b.options?.[0].startTime);
        }
      },
    },
    {
      title: "More about the Volunteer",
      key: "qa",
      dataIndex: "qa",
      render: (v) =>
        v?.map((d, i) => (
          <dl key={i}>
            <dt>{d.question}</dt>
            <dd>{d.answer}</dd>
          </dl>
        )),
    },
    {
      title: "Comments",
      key: "comments",
      dataIndex: "comments",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (v) => (v === "	Cancelled by You" ? "	Cancelled by User" : v),
    },
    {
      title: "Registration Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: fmtDateTime,
      sorter: (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
    },

    {
      key: "actions",
      render: (_, r) =>
        r.status === "To be Confirmed" ? (
          <Space size="small">
            <Button type="link" onClick={() => viewStore.confirmApplication(r.id, true)}>
              Confirm
            </Button>
            <Button type="link" onClick={() => viewStore.confirmApplication(r.id, false)}>
              Reject
            </Button>
          </Space>
        ) : null,
    },
  ];

  return (
    <Layout.Content>
      <PageHeader
        title={viewStore.volunteer?.title || "Volunteer Details"}
        extra={[
          viewStore.volunteer?.status === "Paused" ? (
            <Button key="pause" onClick={() => viewStore.setStatus(false)}>
              Restart
            </Button>
          ) : undefined,

          ["Active", "Scheduled"].includes(viewStore.volunteer?.status) ? (
            <Button key="pause" onClick={() => viewStore.setStatus(true)}>
              Pause
            </Button>
          ) : undefined,

          // ["Active", "Scheduled"].includes(viewStore.volunteer?.status) ? (
          //   <Button key="cancel" onClick={() => viewStore.setStatus(true)}>
          //     Cancel
          //   </Button>
          // ):undefined,

          // ["Active", "Scheduled"].includes(r.status) ?? [
          //   <a key="pause" onClick={() => store.setStatus(r.id, true)}>
          //     Pause
          //   </a>,
          //   <a key="cancel" onClick={() => store.cancelStore.show(r.id, r.schedule)}>
          //     Cancel
          //   </a>,
          // ]:undefined,

          ["Active", "Scheduled"].includes(viewStore.volunteer?.status) ? (
            <Button key="edit" onClick={() => editStore.edit(viewStore.volunteer.id)}>
              Edit
            </Button>
          ) : (
            <Button key="duplicate" onClick={() => editStore.duplicate(viewStore.volunteer.id)}>
              Duplicate
            </Button>
          ),

          <Button loading={viewStore.exporting} key="export" onClick={() => viewStore.onExport()}>
            Export
          </Button>,
        ]}
      ></PageHeader>
      <div className="padding24">
        <Card style={{ marginBottom: 16 }} size="small" loading={viewStore.loading}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Project Host" span={2}>
              {viewStore.volunteer?.host && (
                <div>
                  {viewStore.volunteer?.host?.name && <p>Project host name: {viewStore.volunteer?.host?.name}</p>}
                  {viewStore.volunteer?.host?.website && <p>Website: {viewStore.volunteer?.host?.website}</p>}
                  {viewStore.volunteer?.host?.contact && <p>Contact person: {viewStore.volunteer?.host?.contact}</p>}
                  {viewStore.volunteer?.host?.email && <p>Email address: {viewStore.volunteer?.host?.email}</p>}
                  {viewStore.volunteer?.host?.phone && <p>Phone number: {viewStore.volunteer?.host?.phone}</p>}
                  {viewStore.volunteer?.host?.other && <p>Other information: {viewStore.volunteer?.host?.other}</p>}
                </div>
              )}
            </Descriptions.Item>
            {isDeadline
              ? [
                  <Descriptions.Item label="Deadline">
                    {dayjs(viewStore.volunteer.deadline).format("MMM D")}
                    {viewStore.volunteer?.schedule?.map((x) => dayjs(x).format("MMM D")).join(", ")}
                  </Descriptions.Item>,
                  <Descriptions.Item label="Max Number of Hours">{viewStore.volunteer?.maxHours}</Descriptions.Item>,
                ]
              : [
                  <Descriptions.Item label="Dates">
                    {viewStore.volunteer?.schedule?.map((x) => dayjs(x).format("MMM D")).join(", ")}
                  </Descriptions.Item>,
                  <Descriptions.Item label="Times">
                    <Tag>{viewStore.volunteer?.customizedChoice ? "Flexible" : "Fixed"}</Tag>
                    {viewStore.volunteer?.options?.map((d) => fmtTimes(d.startTime, d.endTime)).join(", ")}
                  </Descriptions.Item>,
                ]}

            <Descriptions.Item label="Location" span={2}>
              {viewStore.volunteer?.location?.addr}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {viewStore.volunteer?.description}
            </Descriptions.Item>
            <Descriptions.Item label="Requirement" span={2}>
              {viewStore.volunteer?.requirements}
            </Descriptions.Item>
            <Descriptions.Item label="Participation Detail" span={2}>
              {viewStore.volunteer?.participationDetail}
            </Descriptions.Item>
            <Descriptions.Item label="Community" span={2}>
              <div>
                {viewStore.volunteer?.recipients?.community?.map((x) => (
                  <Tag key={x.id}>{x.name}</Tag>
                ))}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Card>
        <Card
          size="small"
          title="Applications"
          extra={[
            <Button
              key="be-present"
              onClick={() => myHistory.push(`/volunteer-present/${viewStore.volunteer?.id}`)}
              style={{ marginRight: 10 }}
            >
              Be present
            </Button>,
            <Button
              key="send-mail"
              onClick={() => viewStore.sendHostEmail()}
              loading={viewStore.emailSending}
              style={{ marginRight: 10 }}
              disabled={!viewStore.volunteer?.host?.email || isDeadline}
            >
              Send mail to host
            </Button>,
            <Button key="refresh" onClick={() => viewStore.refreshApplications()} loading={viewStore.applications.loading}>
              Refresh
            </Button>,
          ]}
        >
          <Table
            size="small"
            pagination={false}
            loading={viewStore.applications.loading}
            rowKey="id"
            columns={columns}
            dataSource={viewStore.applications.items}
            expandable={{ childrenColumnName: "-" }}
          />
        </Card>

        {editStore.showDlg && <VolunteerDlg />}
      </div>
    </Layout.Content>
  );
});
